import { trackConfig, type IFramework } from '@/framework';
import { LayerType, type ILayerProps, type IVectorLayerProps, type VectorTextInfo } from '@/version-types';
import { pushHistory, resolveVectorTextLayout, updateLayer } from '../utils';

export interface ITextDecoration {
    /**
     * 设置下划线
     *
     * @param layerID - 图层 ID
     * @param flag - 是否标记
     */
    setUnderline(layerID: string, flag: boolean): ILayerProps | void;

    /**
     * 设置删除线
     *
     * @param layerID - 图层 ID
     * @param flag - 是否标记
     */
    setLineThrough(layerID: string, flag: boolean): ILayerProps | void;
}

export class TextDecoration implements ITextDecoration {
    constructor(private framework: IFramework) {}

    private resolveConflict(textInfo: VectorTextInfo, kind: 'underline' | 'lineThrough', flag: boolean) {
        if (!flag) return textInfo;

        const conflictType = kind === 'underline' ? 'lineThrough' : 'underline';
        const nextTextInfo = { ...textInfo };

        nextTextInfo[conflictType] = false;

        return nextTextInfo;
    }

    /**
     * 更新互斥的下划线或删除线并提交共享布局结果。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param kind - 待更新的装饰字段。
     * @param flag - 是否启用该装饰。
     * @returns 更新后的 Vector；输入不适用时返回 void。
     */
    private set(layerID: string, kind: 'underline' | 'lineThrough', flag: boolean) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return;

        let nextTextInfo: VectorTextInfo = { ...textInfo, [kind]: flag };
        nextTextInfo = this.resolveConflict(nextTextInfo, kind, flag);

        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        const nextLayer = resolveVectorTextLayout(this.framework, { ...prevLayer, data: nextLayerData });

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return nextLayer;
    }

    setUnderline(layerID: string, flag: boolean) {
        return this.set(layerID, 'underline', flag);
    }

    setLineThrough(layerID: string, flag: boolean) {
        return this.set(layerID, 'lineThrough', flag);
    }
}
